import { useState, useMemo, useCallback } from 'react';

/**
 * Normalize string for accent-tolerant search.
 */
const normalize = (s = '') =>
  s
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');

/**
 * Shared FAQ page hook.
 *
 * @param {Object} config
 * @param {Array}  config.questions   - [{ id, category, question, answer, popular, popularRank }]
 * @param {Array}  config.categories  - [{ id, label }]
 * @returns state + handlers for the brand FAQ page wrapper.
 */
export default function useFAQ({ questions = [], categories = [] }) {
  /* ── Pre-expand the first 5 popular questions (sorted by popularRank then index) ── */
  const initialExpanded = useMemo(() => {
    const popular = questions
      .filter((q) => q.popular)
      .sort((a, b) => (a.popularRank ?? 99) - (b.popularRank ?? 99))
      .slice(0, 5)
      .map((q) => q.id);
    return new Set(popular);
  }, [questions]);

  const [expanded, setExpanded] = useState(initialExpanded);
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(null); // null = popular only

  const toggle = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  /* ── Filtered list ──
   * If a search query is present → filter by query across all questions.
   * Else if a category is selected → that category's questions.
   * Else → first 9 popular questions (default landing view).
   */
  const filtered = useMemo(() => {
    const q = normalize(query.trim());
    if (q) {
      return questions.filter((item) => {
        // JSX answers aren't strings — items may carry `searchText` instead.
        const haystack = normalize(`${item.question} ${typeof item.answer === 'string' ? item.answer : ''} ${item.searchText || ''}`);
        return haystack.includes(q);
      });
    }
    if (activeCategory) {
      return questions.filter((item) => item.category === activeCategory);
    }
    return questions
      .filter((item) => item.popular)
      .sort((a, b) => (a.popularRank ?? 99) - (b.popularRank ?? 99))
      .slice(0, 9);
  }, [questions, query, activeCategory]);

  return {
    /* state */
    query,
    setQuery,
    activeCategory,
    setActiveCategory,
    expanded,
    toggle,
    filtered,
    categories,
    /* derived */
    isSearching: query.trim().length > 0,
    hasResults: filtered.length > 0,
  };
}
